import React, { useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import { DEER_PATH, RABBIT_PATH, TURKEY_PATH } from '../components/Header';
import api from '../services/api';
import { LangContext } from '../App';
import { useAuth } from '../contexts/AuthContext';

const HeroScene = () => (
  <svg
    viewBox="0 0 1400 380"
    xmlns="http://www.w3.org/2000/svg"
    style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}
    preserveAspectRatio="xMidYMax slice"
  >
    <defs>
      <linearGradient id="ch-hero-sky" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#0a1f0e" />
        <stop offset="100%" stopColor="#1f3e1f" />
      </linearGradient>
    </defs>
    <rect width="1400" height="380" fill="url(#ch-hero-sky)" />

    {/* Étoiles */}
    <circle cx="80" cy="28" r="1.4" fill="#f5f0e8" opacity="0.85" />
    <circle cx="240" cy="15" r="1" fill="#f5f0e8" opacity="0.75" />
    <circle cx="420" cy="35" r="1.6" fill="#f5f0e8" opacity="0.80" />
    <circle cx="600" cy="20" r="1" fill="#f5f0e8" opacity="0.70" />
    <circle cx="780" cy="10" r="1.3" fill="#f5f0e8" opacity="0.65" />
    <circle cx="950" cy="30" r="1.5" fill="#f5f0e8" opacity="0.80" />
    <circle cx="1100" cy="18" r="1" fill="#f5f0e8" opacity="0.75" />
    <circle cx="1250" cy="40" r="1.4" fill="#f5f0e8" opacity="0.85" />
    <circle cx="1370" cy="12" r="1.2" fill="#f5f0e8" opacity="0.70" />

    {/* Lune */}
    <circle cx="1300" cy="60" r="36" fill="#f4d03f" />
    <circle cx="1300" cy="60" r="36" fill="#1f3e1f" opacity="0.28" />

    {/* ── Épinettes canadiennes gauche ── */}
    {/* Arbre 1 — très grand */}
    <g fill="#0a1f0e">
      <rect x="57" y="290" width="9" height="70" />
      <polygon points="61,155 18,265 104,265" />
      <polygon points="61,185 12,290 110,290" />
      <polygon points="61,128 24,210 98,210" />
      <polygon points="61,105 30,178 92,178" />
    </g>
    {/* Arbre 2 — grand */}
    <g fill="#0a1f0e">
      <rect x="124" y="300" width="8" height="60" />
      <polygon points="128,180 88,275 168,275" />
      <polygon points="128,208 82,300 174,300" />
      <polygon points="128,158 94,235 162,235" />
      <polygon points="128,138 98,205 158,205" />
    </g>
    {/* Arbre 3 — moyen, premier plan */}
    <g fill="#0a1f0e">
      <rect x="183" y="312" width="7" height="48" />
      <polygon points="186,215 150,305 222,305" />
      <polygon points="186,240 144,320 228,320" />
      <polygon points="186,195 154,268 218,268" />
      <polygon points="186,178 158,240 214,240" />
    </g>

    {/* ── Épinettes canadiennes droite ── */}
    {/* Arbre 4 — très grand */}
    <g fill="#0a1f0e">
      <rect x="1210" y="285" width="9" height="75" />
      <polygon points="1214,148 1171,260 1257,260" />
      <polygon points="1214,178 1165,285 1263,285" />
      <polygon points="1214,122 1180,203 1248,203" />
      <polygon points="1214,100 1185,172 1243,172" />
    </g>
    {/* Arbre 5 — grand */}
    <g fill="#0a1f0e">
      <rect x="1278" y="295" width="8" height="65" />
      <polygon points="1282,168 1242,268 1322,268" />
      <polygon points="1282,196 1236,295 1328,295" />
      <polygon points="1282,146 1248,225 1316,225" />
      <polygon points="1282,126 1252,194 1312,194" />
    </g>
    {/* Arbre 6 — moyen, bord droit */}
    <g fill="#0a1f0e">
      <rect x="1345" y="308" width="7" height="52" />
      <polygon points="1348,205 1312,300 1384,300" />
      <polygon points="1348,232 1306,315 1390,315" />
      <polygon points="1348,185 1316,260 1380,260" />
      <polygon points="1348,168 1320,230 1376,230" />
    </g>

    {/* Sol */}
    <line x1="0" y1="360" x2="1400" y2="360" stroke="#0a1f0e" strokeWidth="2" />

    {/* Cerf — grand, gauche, au sol — décalé vers la droite */}
    <g transform="translate(200, 212) scale(0.25)" fill="#0a1f0e">
      <path d={DEER_PATH} />
    </g>

    {/* Dindon — moyen, droite, au sol (587×587 × 0.14 = 82px tall) */}
    <g transform="translate(1100, 278) scale(0.14)" fill="#0a1f0e">
      <path d={TURKEY_PATH} />
    </g>

    {/* Lapin — petit, entre dindon et sapins (582×582 × 0.1 = 58px tall) */}
    <g transform="translate(1010, 302) scale(0.1)" fill="#0a1f0e">
      <path d={RABBIT_PATH} />
    </g>
  </svg>
);

const T = {
  fr: {
    hero: {
      title: 'Connectez chasseurs et propriétaires terriens',
      sousTitre:
        'Location de territoires de chasse par enchère ou à prix fixe. Responsable, légal, sécurisé au Canada.',
      btnTrouver: 'Trouver une terre',
      btnPublier: 'Publier une terre',
    },
    comment: {
      title: 'Comment ça marche',
      steps: [
        {
          icon: '👤',
          title: 'Inscrivez-vous gratuitement',
          desc: 'Créez votre profil chasseur, propriétaire ou guide en 2 minutes.',
        },
        {
          icon: '🗺️',
          title: 'Trouvez votre territoire',
          desc: "Parcourez les terrains par province, espèce ou type d'accès.",
        },
        {
          icon: '🎯',
          title: 'Misez et chassez',
          desc: 'Enchère temps réel ou prix fixe. Contrat PDF automatique signé en ligne.',
        },
      ],
    },
    listings: {
      title: 'Dernières lands disponibles',
      voirTout: 'Voir toutes les terres',
      enchere: 'Enchère',
      prixFixe: 'Prix fixe',
      ha: 'ha',
      capacite: 'chasseurs max',
      misePlancher: 'Mise plancher',
      prixReservation: 'Réservation',
    },
    stats: {
      items: [
        { val: '1+', label: 'Territoires disponibles' },
        { val: '2', label: 'Provinces couvertes (QC + ON)' },
        { val: 'Loi 25 ✓', label: 'Plateforme conforme' },
      ],
      rating: 'Données au lancement — la communauté grandit chaque jour',
    },
    faq: {
      title: 'Questions fréquentes',
      q1q: "Comment fonctionne le système d'enchères ?",
      q1r: "Chaque terrain peut être loué à prix fixe ou par enchère en temps réel. Vous placez votre mise, le système notifie l'enchérisseur en cours. À la clôture, le plus offrant remporte la location et signe un contrat PDF automatique.",
      q2q: 'Les terrains sont-ils vérifiés ?',
      q2r: "Oui. Les propriétaires fournissent leur numéro de lot cadastral et leur déclaration d'identité. Les agriculteurs justifient au moins 4 hectares via le registre foncier provincial. Les guides fournissent 3 références par province d'opération.",
      q3q: 'Quels sont les frais ?',
      // {COMMISSION} est remplacé dans le JSX par la value lue dynamiquement
      // sur GET /api/v1/public/config (clé commission_location_pct).
      q3r: "L'inscription est gratuite. ChasseHunt prélève une commission de {COMMISSION} sur chaque location, traitée via Stripe Connect. Aucun frais caché, aucun abonnement.",
      q3CommissionFallback: 'compétitive',
      q4q: 'Comment obtenir un permis de chasse ?',
      q4intro: 'Le permis est obligatoire et délivré par votre province. Liens utiles :',
      q4qc: 'Québec — MELCCFP : Chasse au Québec',
      q4on: 'Ontario — MNRF : Hunting in Ontario',
    },
    footer: {
      slogan: 'En territoire de chasse responsable',
      // Liens valides uniquement — les anciens « À propos / Blog / Presse /
      // FAQ / Contact » sont retirés (pas de page derrière). À rebrancher
      // quand ces pages seront créées.
      colPlateforme: {
        title: 'Plateforme',
        liens: [
          { label: 'Terres', to: '/lands' },
          { label: 'Guides', to: '/guides' },
          { label: 'Faune', to: '/wildlife' },
          { label: 'Marché', to: '/market' },
          { label: 'Communauté', to: '/community' },
        ],
      },
      colSupport: {
        title: 'Support',
        liens: [{ label: 'Confidentialité', to: '/privacy-policy' }],
      },
      copy: '© 2025 ChasseHunt Inc. — chassehunt.ca / chassehunt.com',
    },
  },
  en: {
    hero: {
      title: 'Connect hunters and landowners',
      sousTitre:
        'Hunt land rental by auction or fixed price. Responsible, legal and secure across Canada.',
      btnTrouver: 'Find land',
      btnPublier: 'List your land',
    },
    comment: {
      title: 'How it works',
      steps: [
        {
          icon: '👤',
          title: 'Sign up for free',
          desc: 'Create your hunter, landowner or guide profile in 2 minutes.',
        },
        {
          icon: '🗺️',
          title: 'Find your territory',
          desc: 'Browse lands by province, species or access type.',
        },
        {
          icon: '🎯',
          title: 'Bid and hunt',
          desc: 'Live auction or fixed price. Automatic PDF contract signed online.',
        },
      ],
    },
    listings: {
      title: 'Latest available lands',
      voirTout: 'See all lands',
      enchere: 'Auction',
      prixFixe: 'Fixed price',
      ha: 'ha',
      capacite: 'hunters max',
      misePlancher: 'Starting bid',
      prixReservation: 'Book for',
    },
    stats: {
      items: [
        { val: '1+', label: 'Available territories' },
        { val: '2', label: 'Provinces covered (QC + ON)' },
        { val: 'Law 25 ✓', label: 'Privacy-compliant platform' },
      ],
      rating: 'Launch data — community growing daily',
    },
    faq: {
      title: 'Frequently asked questions',
      q1q: 'How does the auction system work?',
      q1r: 'Each land can be rented at a fixed price or by live auction. You place your bid, the system notifies the current high bidder. At close, the highest bid wins and an automatic PDF contract is signed.',
      q2q: 'Are the lands verified?',
      q2r: 'Yes. Landowners provide their cadastral lot number and identity declaration. Farmers justify at least 4 hectares via the provincial land registry. Guides provide 3 references per province of operation.',
      q3q: 'What are the fees?',
      q3r: 'Sign-up is free. ChasseHunt takes a {COMMISSION} commission on each rental, processed via Stripe Connect. No hidden fees, no subscription.',
      q3CommissionFallback: 'competitive',
      q4q: 'How do I get a hunting licence?',
      q4intro: 'A licence is mandatory and issued by your province. Useful links:',
      q4qc: 'Quebec — MELCCFP: Hunting in Quebec',
      q4on: 'Ontario — MNRF: Hunting in Ontario',
    },
    footer: {
      slogan: 'In responsible hunting territory',
      colPlateforme: {
        title: 'Platform',
        liens: [
          { label: 'Lands', to: '/lands' },
          { label: 'Guides', to: '/guides' },
          { label: 'Wildlife', to: '/wildlife' },
          { label: 'Market', to: '/market' },
          { label: 'Community', to: '/community' },
        ],
      },
      colSupport: {
        title: 'Support',
        liens: [{ label: 'Privacy', to: '/privacy-policy' }],
      },
      copy: '© 2025 ChasseHunt Inc. — chassehunt.ca / chassehunt.com',
    },
  },
};

const mockTerres = [
  {
    id: 1,
    title: { fr: 'Territoire cerf blanc — Laurentides', en: 'White-tail territory — Laurentides' },
    province: 'QC',
    superficie: 120,
    especes: ['Cerf', 'Orignal'],
    mode: 'enchere',
    prix: 450,
    capacite: 2,
    gradient: 'linear-gradient(135deg, #0c1f0c 0%, #1a3a20 60%, #0a1a0a 100%)',
  },
  {
    id: 2,
    title: { fr: 'Domaine canard — Montérégie', en: 'Duck domain — Montérégie' },
    province: 'QC',
    superficie: 45,
    especes: ['Canard', 'Bernache'],
    mode: 'fixed_price',
    prix: 180,
    capacite: 4,
    gradient: 'linear-gradient(135deg, #0a1c1f 0%, #1a3035 60%, #0a1520 100%)',
  },
  {
    id: 3,
    title: { fr: 'Terre agricole — Outaouais', en: 'Agricultural land — Outaouais' },
    province: 'QC',
    superficie: 200,
    especes: ['Cerf', 'Chevreuil', 'Lièvre'],
    mode: 'enchere',
    prix: 620,
    capacite: 3,
    gradient: 'linear-gradient(135deg, #1a1c0a 0%, #2a3010 60%, #101a08 100%)',
  },
];

function CartePublication({ pub, t, lang, isReal }) {
  const estEnchere = pub.mode === 'enchere';
  // Deep-link to /lands/:id when the data comes from the real /lands API
  // (isReal === true). Static demo cards (fallback when the DB is empty
  // or the fetch fails) have synthetic ids 1/2/3 that wouldn't resolve,
  // so they keep the catch-all link to /lands.
  const target = isReal ? `/lands/${pub.id}` : '/lands';
  return (
    <Link
      to={target}
      style={{
        display: 'block',
        textDecoration: 'none',
        color: 'inherit',
        background: 'var(--couleur-fond-carte)',
        border: '1px solid rgba(201,162,39,0.18)',
        borderRadius: '10px',
        overflow: 'hidden',
        transition: 'transform 0.2s, box-shadow 0.2s',
        cursor: 'pointer',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-5px)';
        e.currentTarget.style.boxShadow = '0 10px 32px rgba(0,0,0,0.5)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = 'none';
      }}
    >
      {/* Thumbnail */}
      <div
        style={{
          height: '140px',
          background: pub.gradient,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
        }}
      >
        <span style={{ fontSize: '2.8rem', opacity: 0.35 }}>🌲</span>

        {/* Mode badge — only shown for static demo cards. Real lands don't
            carry a top-level mode (mode lives on each listing) so the badge
            would be misleading. */}
        {!isReal && (
          <span
            style={{
              position: 'absolute',
              top: '10px',
              right: '10px',
              background: estEnchere ? 'var(--couleur-or)' : 'var(--couleur-vert-accent)',
              color: estEnchere ? 'var(--couleur-fond-carte)' : 'var(--couleur-texte-succes)',
              fontSize: '0.7rem',
              fontWeight: 700,
              padding: '3px 9px',
              borderRadius: '12px',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
            }}
          >
            {estEnchere ? t.enchere : t.prixFixe}
          </span>
        )}

        {/* Province + superficie */}
        <span
          style={{
            position: 'absolute',
            top: '10px',
            left: '10px',
            background: 'rgba(0,0,0,0.55)',
            color: 'rgba(255,255,255,0.85)',
            fontSize: '0.7rem',
            padding: '3px 9px',
            borderRadius: '12px',
          }}
        >
          {pub.province} · {pub.superficie} {t.ha}
        </span>
      </div>

      {/* Content */}
      <div style={{ padding: '1rem' }}>
        <h3
          style={{
            color: 'var(--couleur-texte)',
            fontSize: '0.92rem',
            margin: '0 0 0.55rem',
            lineHeight: 1.35,
            fontWeight: 600,
          }}
        >
          {pub.title[lang]}
        </h3>

        {/* Species tags */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginBottom: '0.9rem' }}>
          {pub.especes.map((e) => (
            <span
              key={e}
              style={{
                background: 'rgba(201,162,39,0.12)',
                color: 'var(--couleur-or)',
                fontSize: '0.7rem',
                padding: '2px 8px',
                borderRadius: '10px',
                border: '1px solid rgba(201,162,39,0.28)',
              }}
            >
              {e}
            </span>
          ))}
        </div>

        {/* Price + capacity — price block only for demo cards (real lands
            don't carry a price at this level). Capacity always shown. */}
        <div
          style={{
            display: 'flex',
            justifyContent: isReal ? 'flex-end' : 'space-between',
            alignItems: 'flex-end',
          }}
        >
          {!isReal && (
            <div>
              <div
                style={{ fontSize: '0.68rem', color: 'rgba(255,255,255,0.4)', marginBottom: '2px' }}
              >
                {estEnchere ? t.misePlancher : t.prixReservation}
              </div>
              <div
                style={{
                  color: 'var(--couleur-or)',
                  fontWeight: 700,
                  fontSize: '1.15rem',
                }}
              >
                {pub.prix} $
              </div>
            </div>
          )}
          {pub.capacite != null && (
            <div
              style={{ color: 'rgba(255,255,255,0.38)', fontSize: '0.74rem', textAlign: 'right' }}
            >
              {pub.capacite} {t.capacite}
            </div>
          )}
        </div>
      </div>
    </Link>
  );
}

const sectionStyle = {
  padding: '4.5rem 2rem',
};

// Styles partagés par la section FAQ (accordéon <details>/<summary>)
const detailsStyle = {
  background: 'rgba(255,255,255,0.025)',
  border: '1px solid rgba(201,162,39,0.15)',
  borderRadius: '8px',
  padding: '1rem 1.2rem',
  marginBottom: '0.7rem',
};

const summaryStyle = {
  fontWeight: 600,
  color: 'var(--couleur-texte)',
  cursor: 'pointer',
  fontSize: '0.95rem',
  // On laisse le triangle/chevron natif du navigateur — behavior
  // universel et accessible clavier sans JavaScript.
};

const faqReponseStyle = {
  color: 'rgba(255,255,255,0.65)',
  fontSize: '0.88rem',
  lineHeight: 1.65,
  margin: '0.8rem 0 0',
};

const faqLienStyle = {
  color: 'var(--couleur-or)',
  textDecoration: 'none',
  borderBottom: '1px solid rgba(201,162,39,0.35)',
};

export default function Accueil() {
  const { lang } = useContext(LangContext);
  const { utilisateur, estConnecte } = useAuth();
  const t = T[lang];

  // The "List your land" CTA in the hero should:
  //   - not logged in        → /signup (create an account, pick landowner/farmer role)
  //   - logged in WITH role  → /register-land (the actual form)
  //   - logged in WITHOUT    → /my-profile (add the role first; verification 24-48h)
  // Mirrors the auth-aware pattern used by Marche, Guides, Communaute CTAs.
  const hasLandsRole =
    utilisateur?.user_type === 'landowner' ||
    utilisateur?.user_type === 'farmer' ||
    (utilisateur?.roles || []).some(
      (r) => ['landowner', 'farmer'].includes(r.role) && r.active !== false
    );
  const publierLink = !estConnecte ? '/signup' : hasLandsRole ? '/register-land' : '/my-profile';

  // Lecture dynamique de la commission de location depuis app_config.
  // Si l'appel échoue (BD vide, network down, etc.) → fallback générique
  // ("commission compétitive" / "competitive") sans bloquer le rendu.
  const [commission, setCommission] = useState(null);

  useEffect(() => {
    api
      .get('/public/config')
      .then((res) => {
        if (typeof res.data?.commission_location_pct === 'number') {
          setCommission(res.data.commission_location_pct);
        }
      })
      .catch(() => {
        /* fallback assuré via la value null */
      });
  }, []);

  // Real lands from the API for the "Latest available lands" hero strip.
  // Falls back to the static mockTerres demo cards if the API call fails
  // or returns an empty list (fresh DB, network down, etc.) so the home
  // page is never visually broken. Each real land's card deep-links to
  // /lands/<uuid> — the new LandDetail page.
  const [realLands, setRealLands] = useState(null);

  useEffect(() => {
    api
      .get('/lands', { params: { limit: 3, status: 'active' } })
      .then((res) => {
        const items = res.data?.lands;
        if (Array.isArray(items) && items.length > 0) {
          setRealLands(items);
        }
      })
      .catch(() => {
        /* fallback to mockTerres via realLands === null */
      });
  }, []);

  // Adapt real-land payload to the shape CartePublication expects.
  // Mock cards carry pre-baked gradients; real cards rotate through the same
  // three palettes so the layout stays consistent.
  const cardGradients = mockTerres.map((t) => t.gradient);
  const lands = realLands
    ? realLands.map((l, i) => ({
        id: l.id,
        title: { fr: l.title, en: l.title },
        province: l.province,
        superficie: l.area_hectares,
        especes: (l.available_species || []).slice(0, 4),
        capacite: l.max_hunters,
        gradient: cardGradients[i % cardGradients.length],
      }))
    : mockTerres;

  // Texte de la commission injecté dans la réponse Q3 de la FAQ.
  // En FR/EN — on compose proprement la value avec son unité.
  const commissionDisplay = commission != null ? `${commission} %` : t.faq.q3CommissionFallback;

  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'var(--couleur-fond)',
        color: 'var(--couleur-texte)',
      }}
    >
      <Header />

      {/* ── Hero ── */}
      <section
        style={{
          position: 'relative',
          minHeight: '380px',
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {/* Full-width scene behind everything */}
        <HeroScene />

        {/* Dark vignette so text stays readable over the scene */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background:
              'radial-gradient(ellipse at 50% 50%, rgba(10,31,14,0.45) 0%, rgba(10,31,14,0.15) 60%, transparent 100%)',
            pointerEvents: 'none',
          }}
        />

        {/* Centered text content */}
        <div
          style={{
            position: 'relative',
            zIndex: 1,
            textAlign: 'center',
            padding: '3.5rem 2rem',
            maxWidth: '680px',
          }}
        >
          <h1
            style={{
              fontFamily: 'Georgia, serif',
              fontSize: 'clamp(1.9rem, 4vw, 3rem)',
              color: '#fff',
              lineHeight: 1.25,
              margin: '0 0 1.3rem',
              textShadow: '0 2px 20px rgba(0,0,0,0.8)',
            }}
          >
            {t.hero.title}
          </h1>
          <p
            style={{
              fontSize: '1.08rem',
              color: 'rgba(255,255,255,0.75)',
              margin: '0 0 2.8rem',
              lineHeight: 1.75,
              textShadow: '0 1px 8px rgba(0,0,0,0.6)',
            }}
          >
            {t.hero.sousTitre}
          </p>
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link
              to="/lands"
              style={{
                background: 'var(--couleur-or)',
                color: 'var(--couleur-fond-carte)',
                padding: '0.85rem 2.2rem',
                borderRadius: '6px',
                textDecoration: 'none',
                fontWeight: 700,
                fontSize: '1rem',
                boxShadow: '0 4px 16px rgba(201,162,39,0.35)',
                transition: 'filter 0.15s',
              }}
            >
              {t.hero.btnTrouver}
            </Link>
            <Link
              to={publierLink}
              style={{
                background: 'transparent',
                color: '#fff',
                border: '2px solid rgba(255,255,255,0.5)',
                padding: '0.85rem 2.2rem',
                borderRadius: '6px',
                textDecoration: 'none',
                fontWeight: 600,
                fontSize: '1rem',
                transition: 'border-color 0.15s',
              }}
            >
              {t.hero.btnPublier}
            </Link>
          </div>
        </div>
      </section>

      {/* ── Stats strip — chiffres lancement honnêtes + rating italique ── */}
      <div
        style={{
          background: 'rgba(201,162,39,0.06)',
          borderTop: '1px solid rgba(201,162,39,0.14)',
          borderBottom: '1px solid rgba(201,162,39,0.14)',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            flexWrap: 'wrap',
            gap: 0,
          }}
        >
          {t.stats.items.map((s, i) => (
            <div
              key={i}
              style={{
                textAlign: 'center',
                padding: '1.4rem 2.5rem',
                borderRight:
                  i < t.stats.items.length - 1 ? '1px solid rgba(201,162,39,0.12)' : 'none',
              }}
            >
              <div
                style={{
                  fontFamily: 'Georgia, serif',
                  fontSize: '1.8rem',
                  color: 'var(--couleur-or)',
                  fontWeight: 700,
                  lineHeight: 1,
                }}
              >
                {s.val}
              </div>
              <div
                style={{
                  fontSize: '0.78rem',
                  color: 'rgba(255,255,255,0.45)',
                  marginTop: '4px',
                  letterSpacing: '0.03em',
                }}
              >
                {s.label}
              </div>
            </div>
          ))}
        </div>
        {/* Note "lancement" — transparence sur le status early-stage */}
        <p
          style={{
            textAlign: 'center',
            margin: 0,
            padding: '0 1.5rem 0.9rem',
            fontSize: '0.72rem',
            color: 'rgba(255,255,255,0.35)',
            fontStyle: 'italic',
          }}
        >
          {t.stats.rating}
        </p>
      </div>

      {/* ── Comment ça marche ── */}
      <section style={{ ...sectionStyle, background: 'var(--couleur-fond)' }}>
        <div style={{ maxWidth: '940px', margin: '0 auto' }}>
          <h2
            style={{
              textAlign: 'center',
              fontFamily: 'Georgia, serif',
              fontSize: '1.9rem',
              color: 'var(--couleur-texte)',
              margin: '0 0 3.5rem',
            }}
          >
            {t.comment.title}
          </h2>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
              gap: '2rem',
            }}
          >
            {t.comment.steps.map((e, i) => (
              <div
                key={i}
                style={{
                  textAlign: 'center',
                  padding: '2rem 1.5rem',
                  background: 'rgba(255,255,255,0.025)',
                  borderRadius: '10px',
                  border: '1px solid rgba(201,162,39,0.1)',
                }}
              >
                {/* Cercle ambré avec emoji (au lieu du numéro 01/02/03) */}
                <div
                  style={{
                    width: '3.4rem',
                    height: '3.4rem',
                    borderRadius: '50%',
                    background: 'rgba(201,162,39,0.1)',
                    border: '2px solid var(--couleur-or)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 1.3rem',
                    fontSize: '1.55rem',
                    lineHeight: 1,
                  }}
                >
                  {e.icon}
                </div>
                <h3
                  style={{
                    color: 'var(--couleur-texte)',
                    fontSize: '1.02rem',
                    margin: '0 0 0.7rem',
                    fontWeight: 600,
                  }}
                >
                  {e.title}
                </h3>
                <p
                  style={{
                    color: 'rgba(255,255,255,0.54)',
                    fontSize: '0.88rem',
                    lineHeight: 1.65,
                    margin: 0,
                  }}
                >
                  {e.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Dernières listings ── */}
      <section style={{ ...sectionStyle, background: 'var(--couleur-fond)', paddingTop: '1rem' }}>
        <div style={{ maxWidth: '980px', margin: '0 auto' }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '2.2rem',
              flexWrap: 'wrap',
              gap: '1rem',
            }}
          >
            <h2
              style={{
                fontFamily: 'Georgia, serif',
                fontSize: '1.9rem',
                color: 'var(--couleur-texte)',
                margin: 0,
              }}
            >
              {t.listings.title}
            </h2>
            <Link
              to="/lands"
              style={{
                color: 'var(--couleur-or)',
                textDecoration: 'none',
                fontSize: '0.88rem',
                fontWeight: 600,
                opacity: 0.85,
              }}
            >
              {t.listings.voirTout} →
            </Link>
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(285px, 1fr))',
              gap: '1.5rem',
            }}
          >
            {lands.map((pub) => (
              <CartePublication
                key={pub.id}
                pub={pub}
                t={t.listings}
                lang={lang}
                isReal={!!realLands}
              />
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ rapide — accordéon natif <details>/<summary> ── */}
      <section style={{ ...sectionStyle, background: 'var(--couleur-fond)' }}>
        <div style={{ maxWidth: '780px', margin: '0 auto' }}>
          <h2
            style={{
              textAlign: 'center',
              fontFamily: 'Georgia, serif',
              fontSize: '1.9rem',
              color: 'var(--couleur-texte)',
              margin: '0 0 2.5rem',
            }}
          >
            {t.faq.title}
          </h2>

          {/* Q1 + Q2 — questions/réponses simples */}
          {[
            { q: t.faq.q1q, r: t.faq.q1r },
            { q: t.faq.q2q, r: t.faq.q2r },
          ].map((item, i) => (
            <details key={i} style={detailsStyle}>
              <summary style={summaryStyle}>{item.q}</summary>
              <p style={faqReponseStyle}>{item.r}</p>
            </details>
          ))}

          {/* Q3 — commission dynamique lue depuis /api/v1/public/config */}
          <details style={detailsStyle}>
            <summary style={summaryStyle}>{t.faq.q3q}</summary>
            <p style={faqReponseStyle}>{t.faq.q3r.replace('{COMMISSION}', commissionDisplay)}</p>
          </details>

          {/* Q4 — permis de chasse avec liens externes officiels */}
          <details style={detailsStyle}>
            <summary style={summaryStyle}>{t.faq.q4q}</summary>
            <div style={faqReponseStyle}>
              <p style={{ margin: '0 0 0.7rem' }}>{t.faq.q4intro}</p>
              <ul style={{ margin: 0, paddingLeft: '1.4rem' }}>
                <li style={{ marginBottom: '0.4rem' }}>
                  🍁{' '}
                  <a
                    href="https://www.quebec.ca/tourisme-et-loisirs/activites-sportives-et-de-plein-air/chasse-au-quebec"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={faqLienStyle}
                  >
                    {t.faq.q4qc} ↗
                  </a>
                </li>
                <li>
                  🍁{' '}
                  <a
                    href="https://www.ontario.ca/page/hunting-ontario"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={faqLienStyle}
                  >
                    {t.faq.q4on} ↗
                  </a>
                </li>
              </ul>
            </div>
          </details>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer
        style={{
          background: 'var(--couleur-fond-pied)',
          borderTop: '1px solid rgba(201,162,39,0.12)',
          padding: '3rem 2rem 2rem',
          marginTop: '1rem',
        }}
      >
        <div style={{ maxWidth: '980px', margin: '0 auto' }}>
          {/* Footer columns — Liens valides uniquement (vers les routes existantes) */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
              gap: '2rem',
              marginBottom: '2.5rem',
            }}
          >
            {[t.footer.colPlateforme, t.footer.colSupport].map((col) => (
              <div key={col.title}>
                <div
                  style={{
                    color: 'var(--couleur-or)',
                    fontWeight: 600,
                    fontSize: '0.85rem',
                    marginBottom: '0.9rem',
                    letterSpacing: '0.04em',
                  }}
                >
                  {col.title}
                </div>
                <ul
                  style={{
                    listStyle: 'none',
                    margin: 0,
                    padding: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '0.5rem',
                  }}
                >
                  {col.liens.map((l) => (
                    <li key={l.to}>
                      <Link
                        to={l.to}
                        style={{
                          color: 'rgba(255,255,255,0.42)',
                          textDecoration: 'none',
                          fontSize: '0.82rem',
                          transition: 'color 0.15s',
                        }}
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.color = 'rgba(255,255,255,0.75)')
                        }
                        onMouseLeave={(e) =>
                          (e.currentTarget.style.color = 'rgba(255,255,255,0.42)')
                        }
                      >
                        {l.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* Bottom bar */}
          <div
            style={{
              borderTop: '1px solid rgba(255,255,255,0.07)',
              paddingTop: '1.5rem',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '0.5rem',
            }}
          >
            <span
              style={{
                fontFamily: 'Georgia, serif',
                fontSize: '1.1rem',
                color: 'var(--couleur-or)',
                letterSpacing: '0.04em',
              }}
            >
              ChasseHunt
            </span>
            <p
              style={{
                color: 'rgba(255,255,255,0.3)',
                fontSize: '0.75rem',
                fontStyle: 'italic',
                margin: 0,
              }}
            >
              {t.footer.slogan}
            </p>
            <p style={{ color: 'rgba(255,255,255,0.2)', fontSize: '0.72rem', margin: 0 }}>
              {t.footer.copy}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
